import { Observable, of, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { generateKey } from 'src/app/shared/util/data-util';
import { MergeData } from './merge-data';

export interface StoreModel {
  [key: string]: StoreContent<any>;
}

export class StoreContent<T> {
  private data: Map<string, T>;
  private keyCreator: ((data: T) => string);
  private allDataSubject = new Subject<T[]>();
  private updateDataSubject = new Subject<T[]>();

  constructor(keyCreator: ((data: T) => any)) {
    this.keyCreator = keyCreator;
  }

  private initMap(): void {
    this.data = new Map<string, T>(null);
  }

  getAll(): Observable<T[]> {
    return of(this.getAllNow());
  }

  getAllNow(): T[] | null {
    if (this.data)
      return [...this.data.values()];
    else
      return null;
  }

  getByKey(key: string): Observable<T | null> {
    if (this.data) {
      return of(this.data.get(key));
    } else
      return of(null);
  }

  getByKeyObject(keyObject: any): Observable<T | null> {
    if (this.data) {
      const key = generateKey(keyObject);
      return of(this.data.get(key));
    } else
      return of(null);
  }

  getByValue(field: string, value: any): Observable<T[]> {
    return this.getAll().pipe(filter(data => data[field] == value));
  }

  store(newData: T[]): void {
    if (this.data == null)
      this.initMap();

    newData.forEach(data => {
      this.data.set(this.generateKeyForData(data), data);
    });
    this.updateDataSubject.next(newData);
    this.allDataSubject.next(this.getAllNow());
  }

  merge(newData: MergeData[], createIfBaseNotExists: boolean = false): void {
    if (this.data == null)
      this.initMap();

    let changed = false;
    const listToPropagate = [];
    newData.forEach(dataToMerge => {
      const data = this.data.get(dataToMerge.key);
      if (data) {
        Object.keys(dataToMerge.data).forEach(objectKey => {
          if (data[objectKey] !== dataToMerge.data[objectKey]) {
            data[objectKey] = dataToMerge.data[objectKey];
            changed = true;
          }
        });
        if (changed) {
          listToPropagate.push(data);
          this.data.set(dataToMerge.key, data);
        }
      } else if (createIfBaseNotExists) {
        listToPropagate.push(dataToMerge.data);
        this.data.set(dataToMerge.key, dataToMerge.data);
      }
    });
    if (listToPropagate.length > 0) {
      this.updateDataSubject.next(listToPropagate);
      this.allDataSubject.next(this.getAllNow());
    }
  }

  delete(object: T): void {
    const key = this.generateKeyForData(object);
    this.data?.delete(key);
    this.allDataSubject.next(this.getAllNow());
  }

  clear(): void {
    this.data?.clear();
    this.data = null;
  }

  subscribeAllDataUpdates(): Observable<T[]> {
    return this.allDataSubject.asObservable();
  }

  subscribeIncrementalUpdates(): Observable<T[]> {
    return this.updateDataSubject.asObservable();
  }

  private generateKeyForData(data: T): string {
    const keyElements = this.keyCreator(data);
    if (keyElements != null && typeof keyElements === 'object')
      return generateKey(keyElements);
    return keyElements;
  }
}
